import { useState } from "react";
import ArrowLeft from "@/assets/icons/LeftArrowGray.svg";
import { CustomButton } from "@/components/ui/Buttons";
import AssignmentDetails from "./components/AssignmentDetails";
import AssignmentHeader from "./components/AssignmentHeader";
import logo from "@/assets/logo/Icon.png";
import { FileDetails } from "@/types/types";
import { FileWithPath, useDropzone } from "react-dropzone";
import DeleteIcon from "@/assets/icons/DeleteIconBlack.svg";
import LoadingStrip from "@/components/ui/LoadinStrip";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { formatFileSize } from "@/utils/formatFileSize";
import Attachment from "@/assets/icons/Attachment.svg";
import { useAppSelector } from "@/redux/hooks/hooks";
import {
  useGetCreatorStatusQuery,
  useGetMeQuery,
} from "@/redux/feature/user/userApi";
import { handleApiRequest } from "@/utils/handleApiRequest";
import {
  useGetAssignmentDeadlineQuery,
  useSubmitAssignmentMutation,
} from "@/redux/feature/assignment/assignmentApi";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { TCreatorStatus } from "@/redux/feature/user/userType";
import moment from "moment-timezone";
import AssignmentMobileScreen from "./components/AssignmentMobileScreen";
import Modal from "@/components/ui/Modal";
import AssignmentSubmissionModal from "./AssignmentSubmissionModal";
import { CREATOR_VIDEO_ACCEPT, isSupportedCreatorVideo } from "@/utils/videoUpload";

const AssignmentSubmission = () => {
  const [files, setFiles] = useState<FileDetails[]>([]);
  const [showProgress, setShowProgress] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const user = useAppSelector((state) => state.auth.user);
  const { data } = useGetMeQuery(user?.email);
  const [isLoading, setIsLoading] = useState(false);

  // Assignment status
  const { data: CreatorStatus } = useGetCreatorStatusQuery(
    user?.email as string,
    {
      skip: !user?.email,
      refetchOnMountOrArgChange: true,
    }
  );

  const creatorStatus: TCreatorStatus = CreatorStatus?.data;

  const isAssignmentPending =
    !creatorStatus?.assignmentStatus?.isRejected &&
    !creatorStatus?.assignmentStatus?.isApproved &&
    creatorStatus?.assignmentStatus?.isInitiated;

  // Video upload
  const userDetails = data?.data;
  const maxFiles = 10;
  const maxSize = 100 * 1024 * 1024; // 100MB
  const accept = CREATOR_VIDEO_ACCEPT;

  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (uploading) return;
    setUploading(true);

    const newFiles = acceptedFiles.slice(0, maxFiles - files.length);
    const uniqueNumber = new Date().getTime();

    let tempFiles: FileDetails[] = [];

    for (const file of newFiles) {
      // Check file size
      if (file.size > maxSize) {
        toast.error("File size exceeds the 100MB limit.");
        setUploading(false);
        return; // Stop processing further if file size is too large
      }

      // Check file type
      if (!isSupportedCreatorVideo(file)) {
        toast.error("Only MP4 or MOV files are allowed.");
        setUploading(false);
        return; // Stop processing further if file type is invalid
      }
      const fileDetails: FileDetails = {
        name: `${uniqueNumber.toString()}-${file.name}`,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file),
        live_url: null,
      };

      tempFiles.push(fileDetails);
    }

    // Batch update the state once at the end
    setFiles((prevFiles) => [...prevFiles, ...tempFiles]);

    for (const fileDetails of tempFiles) {
      try {
        setShowProgress(fileDetails.name);
        const uploadedFile = await UploadFileToS3(fileDetails);

        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.name === fileDetails.name
              ? {
                  ...fileDetails,
                  live_url: uploadedFile.live_url,
                  uploading: false,
                }
              : f
          )
        );
        setShowProgress(null);
      } catch (error) {
        setShowProgress(null);
        console.error(`Failed to upload file: ${fileDetails.name}`, error);
      }
    }

    setUploading(false);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setUploading(false);
    setFiles(updatedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
    noClick: true,
    onDropRejected(fileRejections, event) {
      for (const rejection of fileRejections) {
        for (const error of rejection.errors) {
          if (error.code === "file-too-large") {
            toast.error("File size exceeds the 100MB limit.");
          } else if (error.code === "file-invalid-type") {
            toast.error("Only MP4 or MOV files are allowed.");
          } else {
            toast.error(error.message); // fallback
          }
        }
      }
    },
  });

  const [loading, setLoading] = useState(false);

  // Submit the assignment
  const [submitAssignment] = useSubmitAssignmentMutation();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    const isFilesUploadedProperly = files.every(
      (item) => item.live_url !== null
    );

    if (!isFilesUploadedProperly) {
      toast.error("Please wait until all files are uploaded.");
      return;
    }
    const payload = {
      email: userDetails?.email,
      submittedVideo: files,
      title: "Self Introduction: Passionbits Anchor Talent",
      description: "Self Introduction: Passionbits Anchor Talent",
    };
    setIsLoading(true);
    await handleApiRequest({
      payload: payload,
      requestFunction: submitAssignment,
      onSuccess: () => {
        navigate("/");
        setIsLoading(false);
        toast.success("Assignment Submitted Successfully");
      },
      onError: () => {
        setIsLoading(false);
        toast.error("Something Went Wrong");
      },
    });
  };

  //  handle the submission date

  const { data: AssignmentDeadline } = useGetAssignmentDeadlineQuery(
    user?.email
  );
  const formatDeadline = (deadline: string): string => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return moment(deadline).tz(userTimeZone).format("Do MMMM, YYYY - hh:mm a");
  };
  const deadline = AssignmentDeadline?.data?.deadline
    ? formatDeadline(AssignmentDeadline?.data?.deadline)
    : "Loading...";

  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(true);
  return (
    <div>
      <div className="relative mb-20" {...getRootProps()}>
        <input {...getInputProps()} />
        <AssignmentHeader
          disabled={files.length === 0 || uploading}
          loading={isLoading}
          onSubmit={handleSubmit}
        />
        {/* Titles */}
        <div className="flex flex-col justify-center items-center mt-[60px]">
          <div className="p-2 rounded-lg border border-[#D0D5DD]">
            <img src={logo} className="size-12" alt="Passionbits logo" />
          </div>
          <p className="text-[#667085] text-sm leading-5 tracking-[-1%] font-semibold mt-8 mb-2.5">
            Welcome to passionbits
          </p>
          <h1 className="text-[#1D2939] text-2xl leading-[33px] tracking-[-1%] font-semibold">
            Self Introduction: Passionbits Anchor Talent
          </h1>
        </div>
        <div className="max-w-[1016px] mx-auto mt-[100px]">
          <AssignmentDetails submissionDateAndTime={deadline} />

          {/* File List */}
          {files.length > 0 ? (
            <div className="mt-[50px] p-5 border border-[#C3C3C3] rounded-xl">
              <p className="text-sm font-medium leading-5 mb-[18px]">
                Uploaded files
              </p>
              {files.map((file, index) => (
                <div
                  key={index}
                  className="border border-dashed border-[#C3C3C3] w-full my-4 overflow-hidden flex justify-between items-start rounded-[8px] p-4 bg-[#F0F3F5]"
                >
                  <div className="flex w-full gap-x-[14px] items-start">
                    <div>
                      <Attachment />
                    </div>
                    <div className="w-full">
                      <p className="font-medium text-[#344054] text-caption-large">
                        {file?.name?.length > 40
                          ? `${file?.name?.slice(0, 40)}...`
                          : file?.name}
                      </p>
                      <p className="text-[#475467] text-caption-large">
                        {formatFileSize(file.size)}
                      </p>
                      <div className="pt-1">
                        {showProgress === file.name && <LoadingStrip />}
                      </div>
                      {file.live_url && (
                        <a
                          href={file.live_url}
                          target="_blank"
                          className="text-caption-regular font-medium pt-1"
                          rel="noopener noreferrer"
                        >
                          View file
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end w-fit relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="max-w-[272px] mx-auto mt-9">
              <CustomButton
                fontSize="14px"
                fontWeight="500"
                padding="8px"
                backgroundColor="#17457D"
                borderRadius="8px"
                className="leading-5 w-full"
                onClick={() => {
                  console.log("clicked");
                  const input = document.querySelector(
                    'input[type="file"]'
                  ) as HTMLInputElement;
                  if (input) {
                    input.click();
                  }
                }}
              >
                Upload Video Here
              </CustomButton>
              <p className="text-xs mt-2 text-center text-red-600">
                * Please upload a video in MP4 format only and ensure the file
                size is under 100MB.
              </p>
            </div>
          )}
        </div>
        {isAssignmentPending && (
          <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div className="p-8 rounded-xl border border-[#E5E9EB] bg-white shadow-lg max-w-md w-full mx-4">
              <div className="flex justify-center">
                <div
                  className={`inline-flex items-center gap-1 bg-[#456A97] border border-[#4C380729] rounded-full px-2 py-[2px]`}
                >
                  <div className="bg-white size-1.5 rounded-full" />
                  <p className="text-[11px] font-medium leading-4 tracking-[-1.5%] text-white">
                    Under Review
                  </p>
                </div>
              </div>
              <div className={`mt-4 space-y-2.5 mb-6 text-center`}>
                <h5 className="font-semibold leading-[33px] text-[22px] text-[#1A1A1A]">
                  Waiting for Application Review
                </h5>
                <p className="text-sm leading-6 tracking-[-1%] text-[#667085] max-w-[55ch] mx-auto">
                  Your application has been submitted and is currently under
                  review. We will notify you once it has been evaluated.
                </p>
              </div>
              <div className="flex justify-center mt-6">
                <Link to="/dashboard">
                  <CustomButton
                    fontSize="14px"
                    fontWeight="500"
                    padding="8px 14px"
                    border="1px solid #D0D5DD"
                    borderRadius="45px"
                    backgroundColor="#FFFFFF"
                    color="#344054"
                    variant="outline"
                    className="flex gap-1 items-center"
                  >
                    <ArrowLeft />
                    Back to Dashboard
                  </CustomButton>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      <AssignmentMobileScreen />

      <Modal
        isOpen={isAssignmentModalOpen}
        setOpen={setIsAssignmentModalOpen}
        padding="32px"
        width="738px"
      >
        <AssignmentSubmissionModal />
      </Modal>
    </div>
  );
};

export default AssignmentSubmission;
